import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  EmptyState                                                        */
/* ------------------------------------------------------------------ */

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Icon or illustration slot. */
  icon?: React.ReactNode;
  /** Primary heading. */
  title: string;
  /** Supporting copy. */
  description?: string;
  /** CTA / action button. */
  action?: React.ReactNode;
}

const css = /* css */ `
  .sf-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: var(--sf-space-10) var(--sf-space-6);
    gap: var(--sf-space-3);
    font-family: var(--sf-font-sans);
  }

  .sf-empty-state__icon {
    color: var(--sf-text-muted);
    margin-bottom: var(--sf-space-1);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sf-empty-state__title {
    font-size: var(--sf-text-md);
    font-weight: var(--sf-weight-semibold);
    color: var(--sf-text);
    line-height: var(--sf-leading-tight);
  }

  .sf-empty-state__desc {
    font-size: var(--sf-text-sm);
    color: var(--sf-text-muted);
    max-width: 360px;
    line-height: var(--sf-leading-normal);
  }

  .sf-empty-state__action {
    margin-top: var(--sf-space-2);
  }
`;

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon, title, description, action, className, ...rest }, ref) => {
    injectStyles("sf-empty-state", css);

    const classes = ["sf-empty-state", className ?? ""]
      .filter(Boolean)
      .join(" ");

    return (
      <div ref={ref} className={classes} {...rest}>
        {icon && <div className="sf-empty-state__icon">{icon}</div>}
        <div className="sf-empty-state__title">{title}</div>
        {description && (
          <p className="sf-empty-state__desc">{description}</p>
        )}
        {action && <div className="sf-empty-state__action">{action}</div>}
      </div>
    );
  },
);

EmptyState.displayName = "EmptyState";
export { EmptyState };
